import React, {FormEvent, MutableRefObject, useImperativeHandle, useState} from "react";
import intl from "react-intl-universal";
import {useAppState} from "@stores/AppStateContext";

import {TaskReportFileMeta, TaskReportModelType} from "../../../../stores/taskLists/TaskReportModel";
import type {CalendarTable} from "../../../screens/Lists/components/CalendarTable";
import {FileUploadsManager} from "../../../../stores/models/FileUploadsManager";
import {FileProvider} from "../../../../utils/file/FileProvider";

import {Button} from "../../../components/Button";
import {Textarea} from "../../../components/form/Textarea";
import {FlagIcon} from "../../../components/icons/FlagIcon";
import {InTextLabel} from "../../../components/InTextLabel";
import {FormHint} from "../../../components/FormHint";
import {InfoIcon} from "../../../components/icons/InfoIcon";
import {AttachFiles} from "../../../components/AttachFiles/AttachFiles";

export interface CantCompleteTaskFormProps {
    modelRef: MutableRefObject<TaskReportModelType>;
    calendarTable: CalendarTable;
    taskId: string;
    onGoBack: () => void;
    commentImagesFileUploadManager: FileUploadsManager<TaskReportFileMeta>;
    fileProvider: FileProvider;
}

export interface CantCompleteTaskFormHandle {
    handleClose: () => void;
}

export const CantCompleteTaskForm = React.forwardRef<CantCompleteTaskFormHandle, CantCompleteTaskFormProps>(function CantCompleteTaskForm(
    {
        modelRef,
        calendarTable,
        taskId,
        onGoBack,
        commentImagesFileUploadManager,
        fileProvider,
    }: CantCompleteTaskFormProps,
    ref
) {
    const appState = useAppState();
    const taskReportModel = modelRef.current;
    const task = calendarTable.taskDs?.getTaskById(taskId);
    const taskName = task?.name;
    const taskListUnit = calendarTable.taskListUnitDS.getTaskListUnitById(taskReportModel.taskListUnitId);
    const taskListName = Object.values(taskListUnit?.taskLists ?? {}).find(list => list.id === task?.taskListId)?.name ?? intl.get('taskList.ungrouped.short').d('Ungrouped Tasks');

    const [hasError, setError] = useState<string[]>([]);

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault();
        event.stopPropagation();
        const errors: string[] = [];

        if (taskReportModel.comment === '') {
            errors.push('noComment');
        }

        if (errors.length > 0) {
            setError(errors);
            return;
        }

        await commentImagesFileUploadManager.waitAll();

        if (commentImagesFileUploadManager.json) {
            taskReportModel.setCommentImages(commentImagesFileUploadManager.json);
        }

        await taskReportModel.save();

        appState.setModal(null);
    };

    const handleClose = () => {
        commentImagesFileUploadManager.clear();
        appState.setModal(null);
    };

    useImperativeHandle(ref, () => ({ handleClose }));

    return (
        <form className="TaskReportEdit__form" onSubmit={handleSubmit}>
            <div className="title-large-bold TaskReportEdit__ContentBlock__taskName">{taskName}</div>
            <div className="body-medium TaskReportEdit__ContentBlock__tasklListName">{taskListName}</div>
            <InTextLabel
                size="lg"
                Icon={FlagIcon}
                text={intl.get('taskReportEdit.cant-complete-task.label').d("Can't Complete Task")}
                type="danger"
                className="TaskReportEdit__CantComplete__label"
            />
            <div className="TaskReportEdit__ContentBlock">
                <div className="title-medium TaskReportEdit__ContentBlock__header">
                    {intl.get('taskReportEdit.cant-complete-task.header').d("Why wasn’t this task completed?")}
                    <span className="TaskReportEdit__requiredField">*</span>
                </div>
                <div className="body-medium TaskReportEdit__ContentBlock__description">
                    {intl.get('taskReportEdit.cant-complete-task.description').d('Please explain why the task was not completed. Include details such as the reason, who was informed, and any next steps.')}
                </div>
                <Textarea
                    withBorder
                    value={taskReportModel.comment ?? ''}
                    onChange={(val) => {
                        taskReportModel.setComment(val);
                        setError(prev => prev.filter(e => e !== 'noComment'));
                    }}
                    fixedHeight={120}
                />
                {hasError.includes('noComment') && (
                    <FormHint
                        type="error"
                        size="small"
                        icon={<InfoIcon size={16}/>}            
                        text={intl.get('taskReportEdit.cantCompleteTadk.error').d('Upload is required to mark this task as complete.')}
                        className="TaskReportEdit__FormHint"
                    />
                )}
                <AttachFiles
                    fileUploadsManager={commentImagesFileUploadManager}
                    fileProvider={fileProvider}
                    className="TaskReportEdit__AttachFileButton"
                    label={intl.get('taskReportEdit.attachSupportingFile.button').d('Attach supporting file')}
                />
            </div>
            <div className="TaskReportEdit__Buttons">
                <Button
                    text={intl.get('taskReportEdit.go-back.button').d('Go Back')}
                    style="soft"
                    type="neutral"
                    onClick={onGoBack}
                />
                <Button
                    text={intl.get('taskReportEdit.submit.button').d('Submit')}
                    style="solid"
                    type="primary"
                    loading={taskReportModel.isSaving}
                    className="TaskReportEdit__SaveButton"
                    htmlType="submit"
                />
            </div>
        </form>
    );
});

export default CantCompleteTaskForm;
